import { useMemo } from 'react';

import clsx from 'clsx';

import { useSummaryQuery } from '../../hooks/useSummaryQuery';
import { CustomerType } from '../../types';
import { formatNumber, formatPower } from '../../utils/formatters';
import { assignColorsFromBreakdown } from '../../utils/style';
import { AssetGrowthCard } from './AssetGrowthCard';
import { BigStat } from './BigStat';
import { DataCard } from './DataCard';
import { DomesticPowerCard } from './DomesticPowerCard';
import { GenerationCard } from './GenerationCard';
import { LabelSwitch } from './LabelSwitch';
import { MapGlobal } from './MapGlobal';
import { MapUK } from './MapUK';
import { MegawattGrowthCard } from './MegawattGrowthCard';
import { Overlay } from './Overlay';
import { PowerCard } from './PowerCard';
import {
  bgRadialGradient,
  CONTRACTED_MW,
  generateFakeDomesticLocations,
} from './const';
import logoPath from './logo.svg';

type Props = {
  activeCustomerType: CustomerType;
  setActiveCustomerType?: (customerType: CustomerType) => void;
  isLoadingConfig: boolean;
  viewType: 'global' | 'uk';
};

export default function Content({
  activeCustomerType,
  isLoadingConfig,
  setActiveCustomerType,
  viewType,
}: Props) {
  const { data: summaryData, isLoading: isLoadingSummary } = useSummaryQuery();

  const isLoading = isLoadingSummary || isLoadingConfig;

  const customerTypeData =
    activeCustomerType === CustomerType.Commercial
      ? summaryData?.commercial
      : summaryData?.domestic;

  const deviceStyles = useMemo(
    () =>
      customerTypeData
        ? assignColorsFromBreakdown(customerTypeData.assetPowerBreakdown)
        : undefined,
    [customerTypeData],
  );

  const domesticDeviceStyles = useMemo(
    () =>
      customerTypeData
        ? assignColorsFromBreakdown(customerTypeData.assetCountBreakdown)
        : undefined,
    [customerTypeData],
  );

  const totalPower = formatPower(summaryData?.totalPower, 'M');
  const contractedPower = formatPower(CONTRACTED_MW, 'M');

  return (
    <div
      className={clsx(
        'grid min-h-screen gap-4 text-neutral-5 md:max-h-screen md:overflow-hidden',
        viewType === 'uk' ? 'lg:grid-cols-[55%_45%]' : 'lg:grid-cols-[45%_55%]',
      )}
      style={{
        backgroundColor: 'hsla(266,69%,15%,1)',
        backgroundImage: bgRadialGradient,
      }}
    >
      <div className="flex flex-col justify-evenly gap-4 py-4 pl-4 pr-4 lg:py-10 lg:pr-0 lg:pl-10 xl:pl-20 3xl:gap-8 3xl:py-20">
        <img
          className="w-1/2 self-center lg:self-stretch"
          alt="KrakenFlex logo"
          src={logoPath}
        />
        <dl className="grid grid-cols-3 gap-8 self-center lg:self-stretch">
          <BigStat
            title="Total Power"
            value={totalPower.value}
            unit={totalPower.unit}
            loading={isLoading}
          />
          <BigStat
            title="Contracted"
            value={contractedPower.value}
            unit={contractedPower.unit}
            loading={isLoading}
          />
          <BigStat
            title="Assets"
            value={formatNumber(summaryData?.totalCount, 0)}
            loading={isLoading}
          />
        </dl>
        <div className="flex flex-col gap-4 3xl:gap-8">
          {viewType === 'uk' && setActiveCustomerType && (
            <LabelSwitch
              activeCustomerType={activeCustomerType}
              loading={isLoading}
              onChange={setActiveCustomerType}
            />
          )}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 3xl:gap-8">
            {activeCustomerType === CustomerType.Commercial ? (
              <PowerCard
                data={customerTypeData}
                deviceStyles={deviceStyles}
                loading={isLoading}
              />
            ) : (
              <DomesticPowerCard
                data={customerTypeData}
                deviceStyles={domesticDeviceStyles}
                loading={isLoading}
              />
            )}
            <div className="flex flex-col justify-between gap-4 3xl:gap-8">
              <AssetGrowthCard data={customerTypeData} loading={isLoading} />
              <MegawattGrowthCard data={customerTypeData} loading={isLoading} />
            </div>
          </div>
        </div>
        <div
          className={`border-b border-white border-opacity-80 ${
            isLoading ? '!border-opacity-10' : ''
          }`}
        />
        <DataCard data={summaryData} loading={isLoading} />
      </div>
      {viewType === 'uk' ? (
        <MapUK
          deviceStyles={deviceStyles}
          loading={isLoading}
          locations={
            activeCustomerType === CustomerType.Commercial
              ? customerTypeData?.locations
              : generateFakeDomesticLocations(customerTypeData?.totalCount ?? 0)
          }
        />
      ) : (
        <MapGlobal
          deviceStyles={deviceStyles}
          locations={customerTypeData?.locations ?? []}
        />
      )}
      {viewType === 'uk' && (
        <div className="absolute top-20 right-20 hidden w-52 flex-col gap-4 lg:flex">
          <GenerationCard
            data={summaryData?.generationBreakdown}
            loading={isLoading}
          />
        </div>
      )}
      {summaryData && <Overlay lastUpdated={summaryData?.lastUpdated} />}
    </div>
  );
}
